import { addToolBreadcrumb } from '@tunasong/models'
import {
  VocalLib,
  calibrationMidi,
  computePitchSessionStats,
  generatePitchQuestion,
  toPhraseFeedback,
  type PhraseFeedback,
  type PitchExerciseType,
  type PitchIntervalLevel,
  type PitchQuestion,
  type PitchSessionResult,
  type CaptureDiagnosis,
} from '@tunasong/music-lib'
import { useSearchParams, useUserConfig } from '@tunasong/ui-lib'
import { useEffect, useState } from 'react'
import type { FC } from 'react'
import { RelatedTools, type RelatedTool } from '../related-tools/related-tools.js'
import { runCaptureWindow, type CapturePhase, type CaptureWindowDeps } from './capture-session.js'
import { PitchCalibrationPanel } from './pitch-calibration-panel.js'
import { calibratedRange, initialCalibration, recordCalibration, type CalibrationState } from './pitch-calibration.js'
import { PitchFeedbackPanel } from './pitch-feedback-panel.js'
import { PitchQuestionPanel } from './pitch-question.js'
import { leadInMs, usePitchAudition } from './pitch-training-playback.js'
import { parseExerciseType, parseIntervalLevel } from './pitch-training-params.js'
import { PitchTrainingSummary } from './pitch-training-summary.js'
import { PitchTrainingWelcome } from './pitch-training-welcome.js'
import { useCaptureMonitor } from './use-capture-monitor.js'
import { usePitchCapture } from './use-pitch-capture.js'
import styles from './pitch-training.module.css'

/**
 * Pitch Trainer v1 host — the suite's first reproduction tool.
 *
 * Every capture window is started by a click, never by an effect. Two reasons, and the second is
 * the load-bearing one: a browser will not resume an `AudioContext` without a user gesture, and
 * an effect that re-fires on an unstable dependency would re-open the microphone mid-take. So
 * "Start" runs the first item, and "Next" runs each one after it.
 *
 * Spec: docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §5.8, §6, §7 Phase 1, §9
 */

const ITEMS_PER_SESSION = 10

/** §6: the phrase's length plus a one-second tail, so a slow singer is not cut off. */
const CAPTURE_TAIL_MS = 1000

/** How long a calibration take listens — §5.8 asks for a sustained second, with room to settle. */
const CALIBRATION_CAPTURE_MS = 2500

const wait = (ms: number) => new Promise<void>(resolve => window.setTimeout(resolve, ms))

type SessionState =
  | { phase: 'welcome'; preSelected: PitchExerciseType | null; preSelectedLevel: PitchIntervalLevel | null }
  | { phase: 'calibrating'; calibration: CalibrationState; listening: boolean; diagnosis: CaptureDiagnosis | null }
  | {
      phase: 'asking'
      index: number
      question: PitchQuestion
      reference: number[]
      results: PitchSessionResult[]
      capture: CapturePhase | null
      type: PitchExerciseType
      level: PitchIntervalLevel
    }
  | {
      phase: 'feedback'
      index: number
      question: PitchQuestion
      reference: number[]
      feedback: PhraseFeedback
      diagnosis: CaptureDiagnosis | null
      meanScore: number | null
      results: PitchSessionResult[]
      type: PitchExerciseType
      level: PitchIntervalLevel
    }
  | { phase: 'done'; results: PitchSessionResult[]; type: PitchExerciseType }

/** Round-robin, so "try another exercise" always lands somewhere new. */
const NEXT_EXERCISE: Record<PitchExerciseType, PitchExerciseType> = {
  tone: 'interval',
  interval: 'vocalise',
  vocalise: 'phrase',
  phrase: 'tone',
}

/**
 * §12 authored one outbound crosslink for this tool. Every other tool also carries a
 * `crosslink-*-to-suite` back to the dashboard, but no `crosslink-pitch-training-to-suite` was
 * written and inventing the copy is not this build's to do — flagged for Bowie rather than
 * filled in.
 */
const FOOTER_TOOLS: RelatedTool[] = [
  { targetRoute: '/app/ear-training', lexiconId: 'crosslink-pitch-training-to-ear-training' },
  { targetRoute: '/music-theory', lexiconId: 'crosslink-pitch-training-to-suite' },
]

export const PitchTraining: FC = () => {
  const [searchParams] = useSearchParams()
  const [userConfig, updateConfig] = useUserConfig()
  const { setMuted, startFrames, deviceLabel } = usePitchCapture()
  const monitor = useCaptureMonitor()
  const { playReference, playCountIn } = usePitchAudition()

  const [state, setState] = useState<SessionState>(() => ({
    phase: 'welcome',
    preSelected: parseExerciseType(searchParams.get('mode')),
    preSelectedLevel: parseIntervalLevel(searchParams.get('level')),
  }))

  useEffect(() => {
    addToolBreadcrumb('opened', { tool: 'pitch-training' })
  }, [])

  /**
   * Read with a use-site default of "uncalibrated" — the range is never written into an existing
   * config by a migration, so absence is the normal first-run state and not a missing value.
   */
  const range = userConfig?.musicPreferences?.vocalRange ?? null

  const captureDeps = (
    onPhase: (phase: CapturePhase) => void,
    reference: number[],
    noteMs: number
  ): CaptureWindowDeps => ({
    setMuted,
    startFrames,
    playReference: () => playReference(reference, noteMs),
    playCountIn,
    wait,
    onPhase,
    onFrame: monitor.onFrame,
  })

  const runItem = async (
    index: number,
    question: PitchQuestion,
    reference: number[],
    results: PitchSessionResult[],
    type: PitchExerciseType,
    level: PitchIntervalLevel
  ) => {
    const noteMs = question.holdSeconds * 1000
    monitor.reset()
    setState({ phase: 'asking', index, question, reference, results, capture: null, type, level })

    const frames = await runCaptureWindow(
      captureDeps(
        capture =>
          setState(current =>
            current.phase === 'asking' && current.index === index ? { ...current, capture } : current
          ),
        reference,
        noteMs
      ),
      { leadInMs, captureMs: reference.length * noteMs + CAPTURE_TAIL_MS }
    )

    const score = VocalLib.scorePhrase(VocalLib.segmentNotes(VocalLib.voicedFrames(frames)), reference)
    const feedback = toPhraseFeedback(score)
    /**
     * Only when nothing was heard. "We didn't hear anything" is true of a dead input device, a
     * voice below the level gate and a voice too breathy to track, and sends all three to the
     * same dead end; the frames already tell the three apart.
     */
    const diagnosis = score === null ? VocalLib.diagnoseCapture(frames) : null
    addToolBreadcrumb('sang', {
      tool: 'pitch-training',
      exercise: type,
      heard: !feedback.nothingHeard,
      ...(diagnosis ? { capture: diagnosis.kind } : {}),
    })
    setState({
      phase: 'feedback',
      index,
      question,
      reference,
      feedback,
      diagnosis,
      meanScore: score?.absolute.mean ?? null,
      results,
      type,
      level,
    })
  }

  const startItem = (
    index: number,
    results: PitchSessionResult[],
    type: PitchExerciseType,
    level: PitchIntervalLevel
  ) => {
    const question = generatePitchQuestion(type, level)
    /** §5.8: nothing is auditioned or scored before it sits in the singer's comfortable range. */
    const reference = range ? VocalLib.transposeIntoRange(question.reference, range) : question.reference
    void runItem(index, question, reference, results, type, level)
  }

  const runCalibrationTake = async (calibration: CalibrationState) => {
    monitor.reset()
    setState({ phase: 'calibrating', calibration, listening: true, diagnosis: null })
    const frames = await runCaptureWindow(
      {
        setMuted,
        startFrames,
        /** Nothing to audition — the singer is being measured, not tested. */
        playReference: async () => undefined,
        playCountIn: async () => undefined,
        wait,
        onFrame: monitor.onFrame,
      },
      { leadInMs, captureMs: CALIBRATION_CAPTURE_MS }
    )

    const measuredMidi = calibrationMidi(frames)
    const next = recordCalibration(calibration, measuredMidi)
    setState({
      phase: 'calibrating',
      calibration: next,
      listening: false,
      diagnosis: measuredMidi === null ? VocalLib.diagnoseCapture(frames) : null,
    })

    const measured = next.step === 'done' ? calibratedRange(next) : null
    if (measured) {
      updateConfig('musicPreferences', { ...userConfig?.musicPreferences, vocalRange: measured })
      addToolBreadcrumb('calibrated', { tool: 'pitch-training' })
    }
  }

  if (state.phase === 'welcome') {
    return (
      <div className={styles.container}>
        <PitchTrainingWelcome
          preSelected={state.preSelected}
          preSelectedLevel={state.preSelectedLevel}
          range={range}
          onStart={(type, level) => startItem(0, [], type, level)}
          onCalibrate={() =>
            setState({ phase: 'calibrating', calibration: initialCalibration, listening: false, diagnosis: null })
          }
        />
        <RelatedTools tools={FOOTER_TOOLS} />
      </div>
    )
  }

  if (state.phase === 'calibrating') {
    return (
      <div className={styles.container}>
        <PitchCalibrationPanel
          state={state.calibration}
          listening={state.listening}
          diagnosis={state.diagnosis}
          meter={{ frame: monitor.frame, noiseFloorRms: monitor.noiseFloorRms, deviceLabel }}
          onSing={() => void runCalibrationTake(state.calibration)}
          onCancel={() => setState({ phase: 'welcome', preSelected: null, preSelectedLevel: null })}
          onDone={() => setState({ phase: 'welcome', preSelected: null, preSelectedLevel: null })}
        />
      </div>
    )
  }

  if (state.phase === 'asking') {
    return (
      <div className={styles.container}>
        <PitchQuestionPanel
          question={state.question}
          reference={state.reference}
          index={state.index}
          total={ITEMS_PER_SESSION}
          phase={state.capture}
          meter={{ frame: monitor.frame, noiseFloorRms: monitor.noiseFloorRms, deviceLabel }}
          onHearIt={() => void playReference(state.reference, state.question.holdSeconds * 1000)}
        />
      </div>
    )
  }

  if (state.phase === 'feedback') {
    const results: PitchSessionResult[] = [...state.results, { question: state.question, meanScore: state.meanScore }]
    return (
      <div className={styles.container}>
        <PitchQuestionPanel
          question={state.question}
          reference={state.reference}
          index={state.index}
          total={ITEMS_PER_SESSION}
          phase={null}
          meter={{ frame: monitor.frame, noiseFloorRms: monitor.noiseFloorRms, deviceLabel }}
          onHearIt={() => void playReference(state.reference, state.question.holdSeconds * 1000)}
        />
        <PitchFeedbackPanel
          feedback={state.feedback}
          diagnosis={state.diagnosis}
          onRetry={() =>
            void runItem(state.index, state.question, state.reference, state.results, state.type, state.level)
          }
          onNext={() => {
            if (state.index + 1 >= ITEMS_PER_SESSION) {
              setState({ phase: 'done', results, type: state.type })
              return
            }
            startItem(state.index + 1, results, state.type, state.level)
          }}
        />
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <PitchTrainingSummary
        exerciseType={state.type}
        stats={computePitchSessionStats(state.results)}
        onRestart={() => startItem(0, [], state.type, 1)}
        onSwitchExercise={last =>
          setState({ phase: 'welcome', preSelected: NEXT_EXERCISE[last], preSelectedLevel: null })
        }
      />
      <RelatedTools tools={FOOTER_TOOLS} />
    </div>
  )
}
